import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, LinkSimple, Sparkle } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { coachSessionsEndpoints } from '../../../endpoints/coachSessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import styles from './PhysicalBlock.module.scss';

type Mode = 'link' | 'inline';

/**
 * Dialog collegamento/creazione scheda palestra per blocco Physical.
 * Spec v1.1.1 sez 11 (Approccio C Ibrido, righe 880-958).
 */
export const GymLinkDialog: FC<{
  sessionId: string;
  blockOrder: number;
  onClose: () => void;
}> = ({ sessionId, blockOrder, onClose }) => {
  const [mode, setMode] = useState<Mode>('link');
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();

  const { data: workoutsData } = useQuery({
    queryKey: ['programmingWorkouts'],
    queryFn: () =>
      endpoints.getProgrammingWorkouts({ limit: 100 }).then((r) => r.data),
    enabled: mode === 'link',
  });

  const [selectedId, setSelectedId] = useState('');
  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineDuration, setInlineDuration] = useState<number>(30);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });

  const linkMut = useMutation({
    mutationFn: () =>
      coachSessionsEndpoints
        .linkGym(sessionId, blockOrder, { gymSessionId: selectedId })
        .then((r) => r.data),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const inlineMut = useMutation({
    mutationFn: () =>
      coachSessionsEndpoints
        .createGymInline(sessionId, blockOrder, {
          title: inlineTitle,
          durationMinutes: inlineDuration,
        })
        .then((r) => r.data),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // L'endpoint /programming/workouts ritorna `IProgrammingPaginated<IWorkout>`
  // → la lista è in `.docs`.
  const workouts: any[] = Array.isArray(workoutsData)
    ? workoutsData
    : (workoutsData?.docs ?? []);

  return (
    <div className={styles.dialogBackdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h3>Scheda palestra per questo blocco</h3>
          <button type="button" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.dialogModeTabs}>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === 'link' ? styles.modeTabActive : ''}`}
            onClick={() => setMode('link')}
          >
            <LinkSimple size={14} /> Collega esistente
          </button>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === 'inline' ? styles.modeTabActive : ''}`}
            onClick={() => setMode('inline')}
          >
            <Sparkle size={14} /> Crea al volo
          </button>
        </div>

        <div className={styles.dialogBody}>
          {mode === 'link' && (
            <>
              <label className={styles.dialogLabel}>
                Seleziona scheda dalla library
              </label>
              <select
                className={styles.dialogSelect}
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">— Seleziona —</option>
                {workouts.map((w: any) => (
                  <option key={w._id} value={w._id}>
                    {w.name ?? w.title ?? w._id} ·{' '}
                    {w.totalDurationMinutes ?? w.durationMinutes ?? '—'}'
                  </option>
                ))}
              </select>
            </>
          )}
          {mode === 'inline' && (
            <>
              <label className={styles.dialogLabel}>Nome scheda</label>
              <input
                className={styles.dialogInput}
                value={inlineTitle}
                onChange={(e) => setInlineTitle(e.target.value)}
                placeholder="Es. Velocità + reattività MD-3"
              />
              <label className={styles.dialogLabel}>Durata stimata (min)</label>
              <input
                type="number"
                className={styles.dialogInput}
                value={inlineDuration}
                onChange={(e) => setInlineDuration(Number(e.target.value))}
                min={5}
                max={180}
              />
              <div className={styles.dialogHint}>
                La scheda viene salvata automaticamente anche nella
                Programmazione Palestra.
              </div>
            </>
          )}
        </div>

        <div className={styles.dialogFooter}>
          <CustomButton color="outline" size="sm" onClick={onClose}>
            Annulla
          </CustomButton>
          {mode === 'link' ? (
            <CustomButton
              color="primary"
              size="sm"
              disabled={!selectedId || linkMut.isPending}
              isLoading={linkMut.isPending}
              onClick={() => linkMut.mutate()}
            >
              Collega
            </CustomButton>
          ) : (
            <CustomButton
              color="primary"
              size="sm"
              disabled={!inlineTitle.trim() || inlineMut.isPending}
              isLoading={inlineMut.isPending}
              onClick={() => inlineMut.mutate()}
            >
              Crea e collega
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
};
